import React, { useCallback, useEffect, useState } from 'react';
import type { Collection } from '../domain/collections';
import { isDomainError } from '../domain/errors';
import type { PersonRef } from '../domain/people';
import { useRepositories } from '../hooks/useRepositories';
import { fullName } from '../utils/people';
import { ActionError, ErrorState } from './PageStates';
import UserAvatar from './UserAvatar';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Make an open collection private (015 FR-019–FR-021): everyone who sees it
 * now, all kept to start; the unchecked lose it. If the list changed while I
 * was deciding, the server refuses and the list is shown again — my choices
 * kept, anyone new checked.
 */
export default function MakePrivateDialog({
  collection,
  open,
  onOpenChange,
  onDone,
}: {
  collection: Collection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: (collection: Collection) => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-3xl border-none bg-background">
        {open && (
          <KeepList
            collection={collection}
            onDone={(updated) => {
              onOpenChange(false);
              onDone(updated);
            }}
          />
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

type Load =
  | { state: 'loading' }
  | { state: 'failed'; error: unknown }
  | { state: 'ready'; people: PersonRef[] };

function KeepList({
  collection,
  onDone,
}: {
  collection: Collection;
  onDone: (collection: Collection) => void;
}) {
  const { audience } = useRepositories();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  /** People I unchecked; everyone else shown is kept, newcomers included. */
  const [dropped, setDropped] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetchList = useCallback(async () => {
    setLoad({ state: 'loading' });
    try {
      setLoad({ state: 'ready', people: await audience.makePrivatePreview(collection.id) });
    } catch (e) {
      setLoad({ state: 'failed', error: e });
    }
  }, [audience, collection.id]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const shown = load.state === 'ready' ? load.people : [];

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await audience.makePrivate(
        collection.id,
        shown.filter((p) => !dropped.has(p.id)).map((p) => p.id),
        shown.map((p) => p.id),
      );
      onDone(updated);
    } catch (e) {
      setError(e);
      if (isDomainError(e, 'keepListChanged')) await fetchList();
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string, keep: boolean) =>
    setDropped((prev) => {
      const next = new Set(prev);
      if (keep) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Make {collection.name} private?</AlertDialogTitle>
        <AlertDialogDescription>
          Only the people you keep will still see it and its listings. Anyone you uncheck loses it.
          A private collection can't be made open again.
        </AlertDialogDescription>
      </AlertDialogHeader>

      {load.state === 'loading' ? (
        <div role="status" aria-label="Loading who sees it" className="space-y-2">
          <Skeleton className="h-12 rounded-2xl" />
          <Skeleton className="h-12 rounded-2xl" />
        </div>
      ) : load.state === 'failed' ? (
        <ErrorState message="Couldn't load who sees it." onRetry={fetchList} />
      ) : shown.length === 0 ? (
        <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
          Nobody else sees {collection.name} right now.
        </p>
      ) : (
        <fieldset>
          <legend className="mb-3 text-sm font-semibold text-foreground">Keep seeing it:</legend>
          <ul className="max-h-72 space-y-2 overflow-y-auto">
            {shown.map((p) => {
              const id = `keep-${p.id}`;
              return (
                <li key={p.id} className="flex items-center gap-3 rounded-2xl bg-muted px-4 py-3">
                  <Checkbox
                    id={id}
                    checked={!dropped.has(p.id)}
                    onCheckedChange={(on) => toggle(p.id, on === true)}
                  />
                  <UserAvatar person={p} className="size-7" textClassName="text-[10px]" />
                  <Label htmlFor={id} className="flex-1 cursor-pointer font-medium">
                    {fullName(p)}
                  </Label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      )}

      {error !== null && <ActionError error={error} />}

      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
        <Button
          className="rounded-full"
          disabled={busy || load.state !== 'ready'}
          onClick={confirm}
        >
          Make private
        </Button>
      </AlertDialogFooter>
    </>
  );
}
